import React from "react";
import { ReactComponent as HomeIcon } from "../assets/icons/home-line-new.svg";
import { ReactComponent as LightningIcon } from "../assets/icons/lightning-01-new.svg";
import { ReactComponent as UploadIcon } from "../assets/icons/upload-01-new.svg";
import { ReactComponent as BookOpenIcon } from "../assets/icons/book-open-01-new.svg";
import { ReactComponent as AdminIcon } from "../assets/icons/admin-new.svg";
import { ReactComponent as LayoutGridIcon } from "../assets/icons/layout-grid-02.svg";
import { ReactComponent as LayersIcon } from "../assets/icons/layers-icon.svg";
import { ReactComponent as MessageChatSquare } from "../assets/icons/message-chat-square.svg";
import { ReactComponent as GaugeIcon } from "../assets/icons/gauge.svg";
import { ReactComponent as ToolIcon } from "../assets/icons/tool-02.svg";

// Shared nav config — single source of truth for the VerticalNav sidebar and the mobile hamburger menu. Weather, Projects and Public Profiles left the rail for the Admin page's quick links; their routes are unchanged.
export interface NavLinkConfig {
  path: string;
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Extra icon classes for the sidebar rendering (light background) */
  sidebarIconClassName?: string;
  /** Extra icon classes for the mobile menu rendering (dark background) */
  mobileIconClassName?: string;
  /** "creator": only while creator mode is on (or a full Admin) — see docs/plans/CREATOR_MODE_PLAN.md */
  visibility: "always" | "admin" | "subscriptions" | "creator";
  isActive: (pathname: string) => boolean;
}

export const NAV_LINKS: NavLinkConfig[] = [
  {
    path: "/home",
    label: "Asset Groups",
    Icon: HomeIcon,
    visibility: "always",
    isActive: (pathname) => pathname === "/home",
  },
  {
    path: "/home/model-templates",
    label: "Model Templates",
    Icon: LightningIcon,
    // Templates only create models, so the link shows with creator mode on (non-admins then see the historical templates only)
    visibility: "creator",
    // Old /home/models and /home/model-garden redirect here, so only the new path needs matching
    isActive: (pathname) => pathname.includes("/home/model-templates"),
  },
  {
    path: "/home/data-models",
    label: "Data & Models",
    Icon: LayersIcon,
    // Non-admins see their datasets here; the page hides models from them
    visibility: "always",
    isActive: (pathname) => pathname.includes("/home/data-models"),
  },
  {
    path: "/home/dashboards",
    label: "Dashboards",
    Icon: GaugeIcon,
    visibility: "admin",
    // Old /home/dashboard and /home/operations redirect here
    isActive: (pathname) => pathname.includes("/home/dashboards"),
  },
  {
    path: "/home/custom-views",
    label: "Custom Views",
    // A dashboard grid on the same 24px canvas as its neighbours; bar-line-chart drew small and high inside a 32px box wrapped in a Figma filter
    Icon: LayoutGridIcon,
    // Org members see their org's views read-only; the page hides edit controls from them
    visibility: "always",
    // Old /home/forecast-views and /home/composed-forecasts redirect here
    isActive: (pathname) => pathname.includes("/home/custom-views"),
  },
  {
    path: "/home/upload",
    label: "Upload",
    Icon: UploadIcon,
    visibility: "always",
    isActive: (pathname) => pathname.includes("/upload") && !pathname.includes("/dataupload"),
  },
  {
    path: "/home/tools",
    label: "Tools",
    Icon: ToolIcon,
    visibility: "always",
    // The tools themselves live at their own /home/* routes, so each one lights this entry
    isActive: (pathname) =>
      pathname.includes("/home/tools") ||
      pathname.includes("/home/email-schedule") ||
      pathname.includes("/home/delivery-logs") ||
      pathname.includes("/home/watershed-delineation") ||
      pathname.includes("/home/forecast-verification"),
  },
  {
    path: "/home/resources",
    label: "Resources",
    Icon: BookOpenIcon,
    visibility: "always",
    isActive: (pathname) => pathname.includes("/home/resources"),
  },
  {
    path: "/home/ai-analyst",
    label: "AI Analyst",
    Icon: MessageChatSquare,
    visibility: "subscriptions",
    isActive: (pathname) => pathname.includes("/home/ai-analyst"),
  },
  {
    path: "/home/admin",
    label: "Admin",
    Icon: AdminIcon,
    visibility: "admin",
    isActive: (pathname) => pathname.includes("/home/admin"),
  },
];

export const getVisibleNavLinks = (
  isAdmin: boolean,
  subscriptionsEnabled: boolean,
  canCreate: boolean
): NavLinkConfig[] =>
  NAV_LINKS.filter(
    (link) =>
      link.visibility === "always" ||
      (link.visibility === "admin" && isAdmin) ||
      (link.visibility === "subscriptions" && subscriptionsEnabled) ||
      (link.visibility === "creator" && canCreate)
  );
